import React, { useState, useEffect } from "react";
import CountUp from "../../components/ui/CountUp";
import { motion } from "framer-motion";

import {
  Search,
  ChevronDown,
  Settings,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle,
  Plus,
  FileText,
  Calendar,
  MessageSquare,
  TrendingUp,
  UserCheck,
  BookOpen,
  Sparkles,
} from "lucide-react";

const students = [
  {
    id: "ST2024001",
    name: "Arjun Kumar",
    avatar: "/photo.png",
    academic: 92,
    attendance: 88,
    homework: 85,
    status: "On Track",
  },
  {
    id: "ST2024002",
    name: "Priya Verma",
    avatar: "/photo1.png",
    academic: 78,
    attendance: 95,
    homework: 91,
    status: "Excellent",
  },
  {
    id: "ST2024003",
    name: "Rahul Singh",
    avatar: "/photo2.png",
    academic: 65,
    attendance: 70,
    homework: 60,
    status: "Needs Attention",
  },
  {
    id: "ST2024004",
    name: "Ananya Gupta",
    avatar: "/photo3.png",
    academic: 95,
    attendance: 92,
    homework: 98,
    status: "Excellent",
  },
  {
    id: "ST2024005",
    name: "Vikram Patel",
    avatar: "/photo4.png",
    academic: 88,
    attendance: 85,
    homework: 82,
    status: "On Track",
  },
  {
    id: "ST2024006",
    name: "Sneha Reddy",
    avatar: "/photo.png",
    academic: 90,
    attendance: 88,
    homework: 85,
    status: "On Track",
  },
];

const classPerformanceData = [
  { subject: "Math", score: 85 },
  { subject: "Science", score: 78 },
  { subject: "English", score: 92 },
  { subject: "History", score: 88 },
  { subject: "Art", score: 95 },
];

const StudentTrackerPage = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredStudents, setFilteredStudents] = useState(students);
  const [filters, setFilters] = useState({
    grade: "",
    subject: "",
    timeRange: "",
    alerts: "",
  });

  useEffect(() => {
    let filtered = students;

    if (searchQuery) {
      filtered = filtered.filter(
        (student) =>
          student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          student.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply other filters as needed
    setFilteredStudents(filtered);
  }, [searchQuery, filters]);

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "students", label: "Students" },
    { id: "reports", label: "Reports" },
    { id: "goals", label: "Goals" },
    { id: "settings", label: "Settings" },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Excellent":
        return "bg-green-100 text-green-700";
      case "On Track":
        return "bg-blue-100 text-blue-700";
      case "Needs Attention":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex gap-6">
          {/* Main Content Area */}
          <div className="flex-1 space-y-6">
            {/* Header Tabs */}
            <div className="flex items-center justify-between">
              <div className="flex border-b border-gray-200">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                      activeTab === tab.id
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <FileText className="w-4 h-4" />
                  Export
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <Settings className="w-4 h-4" />
                  Settings
                </button>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  <div className="relative">
                    <select
                      className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white transition-all duration-200 hover:border-gray-300"
                      value={filters.grade}
                      onChange={(e) =>
                        setFilters({ ...filters, grade: e.target.value })
                      }
                    >
                      <option value="">All Grades</option>
                      <option value="grade-1">Grade 1</option>
                      <option value="grade-2">Grade 2</option>
                      <option value="grade-3">Grade 3</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-gray-500" />
                  <div className="relative">
                    <select className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white">
                      <option>All Subjects</option>
                      <option>Math</option>
                      <option>Science</option>
                      <option>English</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <div className="relative">
                    <select className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white">
                      <option>This Week</option>
                      <option>This Month</option>
                      <option>This Quarter</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-gray-500" />
                  <div className="relative">
                    <select className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white">
                      <option>All Alerts</option>
                      <option>High Priority</option>
                      <option>Medium Priority</option>
                      <option>Low Priority</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64 pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  <CountUp
                    from={0}
                    to={156}
                    separator=","
                    direction="up"
                    duration={1}
                    className="count-up-text"
                  />
                </div>
                <div className="text-gray-600 text-sm">Total Students</div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  <CountUp
                    from={0}
                    to={87}
                    separator=","
                    direction="up"
                    duration={1}
                    className="count-up-text"
                  />
                  %
                </div>
                <div className="text-gray-600 text-sm">Average Attendance</div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  <CountUp
                    from={0}
                    to={12}
                    separator=","
                    direction="up"
                    duration={1}
                    className="count-up-text"
                  />
                </div>
                <div className="text-gray-600 text-sm">Need Attention</div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  <CountUp
                    from={0}
                    to={92}
                    separator=","
                    direction="up"
                    duration={1}
                    className="count-up-text"
                  />
                  %
                </div>
                <div className="text-gray-600 text-sm">Homework Done</div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Class Performance Chart */}
             
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">
                  Class Performance
                </h3>
                <div className="flex items-end justify-between h-64 px-4">
                  {classPerformanceData.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-center gap-2 flex-1"
                    >
                      <div
                        className="relative w-12 bg-gray-100 rounded-t-lg flex items-end"
                        style={{ height: "200px" }}
                      >
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: `${(item.score / 100) * 200}px` }}
                          transition={{
                            duration: 1,
                            ease: "easeOut",
                            delay: index * 0.2,
                          }}
                          className="w-full bg-orange-500 rounded-t-lg flex items-end justify-center pb-2"
                        >
                          <span className="text-white text-xs font-medium">
                            {item.score}
                          </span>
                        </motion.div>
                      </div>
                      <span className="text-sm text-gray-600 font-medium">
                        {item.subject}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-center">
                  <span className="text-sm text-blue-600">■ Score</span>
                </div>
              </div>
              {/* Attendance Overview */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6">
                  Attendance Overview
                </h3>
                <div className="flex items-center justify-center mb-6">
                 <div className="relative w-32 h-32">
  <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 36 36">
    <motion.path
      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
      fill="none"
      stroke="#e5e7eb"
      strokeWidth="2"
    />
    <motion.path
      initial={{ strokeDasharray: "0,100" }}
      animate={{ strokeDasharray: "75,100" }}
      transition={{ duration: 1 }}
      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
      fill="none"
      stroke="#10b981"
      strokeWidth="2"
    />
    <motion.path
      initial={{ strokeDasharray: "0,100", strokeDashoffset: "-75" }}
      animate={{ strokeDasharray: "15,100", strokeDashoffset: "-75" }}
      transition={{ duration: 1, delay: 0.3 }}
      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
      fill="none"
      stroke="#f59e0b"
      strokeWidth="2"
    />
    <motion.path
      initial={{ strokeDasharray: "0,100", strokeDashoffset: "-90" }}
      animate={{ strokeDasharray: "10,100", strokeDashoffset: "-90" }}
      transition={{ duration: 1, delay: 0.6 }}
      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
      fill="none"
      stroke="#ef4444"
      strokeWidth="2"
    />
  </svg>
</div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Completed</span>
                    </div>
                    <span className="text-sm font-medium">75%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">In Progress</span>
                    </div>
                    <span className="text-sm font-medium">15%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Not Started</span>
                    </div>
                    <span className="text-sm font-medium">10%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Student Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStudents.map((student, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={student.avatar || "/placeholder.svg"}
                      alt={student.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {student.name}
                      </h3>
                      <p className="text-sm text-gray-500">ID: {student.id}</p>
                    </div>
                    <div className="ml-auto">
                      <span
                        className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(
                          student.status
                        )}`}
                      >
                        {student.status}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {student.academic}%
                      </div>
                      <div className="text-xs text-gray-500">Academic</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {student.attendance}%
                      </div>
                      <div className="text-xs text-gray-500">Attendance</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {student.homework}%
                      </div>
                      <div className="text-xs text-gray-500">Homework</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium">
                      View Details
                    </button>
                    <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                      <Plus className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-80 flex-shrink-0 space-y-6 sticky top-6 self-start">
            {/* Smart Alerts */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4">Smart Alerts</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      Low Performance Alert
                    </h4>
                    <p className="text-gray-600 text-xs mt-1">
                      4 students need attention in Math
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      Attendance Warning
                    </h4>
                    <p className="text-gray-600 text-xs mt-1">
                      Rahul missed 3 classes this week
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      Improvement Notice
                    </h4>
                    <p className="text-gray-600 text-xs mt-1">
                      Ananya improved in Science by 25%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors duration-200">
                  <div className="flex items-center gap-3">
                    <Plus className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">
                      Add New Student
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 rotate-[-90deg]" />
                </button>
                <button className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors duration-200">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">
                      Generate Report
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 rotate-[-90deg]" />
                </button>
                <button className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors duration-200">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">
                      Schedule Meeting
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 rotate-[-90deg]" />
                </button>
                <button className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors duration-200">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">
                      Send Message
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 rotate-[-90deg]" />
                </button>
              </div>
            </div>

            {/* AI Insights */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h3 className="font-bold text-gray-900">AI Insights</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      Performance Trend
                    </h4>
                    <p className="text-gray-600 text-xs mt-1">
                      Class average improved by 12% this month
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <UserCheck className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      Student Behavior
                    </h4>
                    <p className="text-gray-600 text-xs mt-1">
                      Increased participation in group activities
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      Homework Progress
                    </h4>
                    <p className="text-gray-600 text-xs mt-1">
                      85% of assignments submitted on time
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentTrackerPage;
